import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Box,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BoltIcon from "@mui/icons-material/Bolt";
import axios from "axios";
import { WEBSERVICE, showToast } from "../CoreDataService";

const MIN_AMOUNT = 0.0001;

const darkColors = {
  background: "#121212",
  text: "#ffffff",
  textSecondary: "#b0b0b0",
  primary: "#90caf9",
  border: "#333333",
  surfaceAlt: "#2a2a2a",
};

const lightColors = {
  background: "#ffffff",
  text: "#212529",
  textSecondary: "#6c757d",
  primary: "#1976d2",
  border: "#dee2e6",
  surfaceAlt: "#eef2f7",
};

/**
 * PlaidBuyPopup
 *
 * Props:
 *   open                - boolean
 *   onClose             - function
 *   accounts            - array of plaid accounts [{ id, accountId, accountNo, accountType, institutionName }]
 *   counterCurrencyCode - string e.g. "BAT" — the currency being bought
 *   counterCurrencyId   - number | string
 *   userUuid            - string
 *   theme               - "light" | "dark"
 */
const PlaidBuyPopup = ({
  open,
  onClose,
  accounts = [],
  counterCurrencyCode,
  counterCurrencyId,
  userUuid = "",
  theme = "light",
  btncolor = "#1976d2",
}) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const colors = theme === "dark" ? darkColors : lightColors;
  const isDark = theme === "dark";

  const [selectedAccountId, setSelectedAccountId] = useState("");

  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");

  const [baseCurrencyList, setBaseCurrencyList] = useState([]);
  const [selectedBase, setSelectedBase] = useState("");
  const [baseLoading, setBaseLoading] = useState(false);

  const [marketPrice, setMarketPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState("");

  const [tokenType, setTokenType] = useState("");

  const [buyLoading, setBuyLoading] = useState(false);
  const [buyError, setBuyError] = useState("");

  // ── Fetch the counter currency's default token type (ERC/TRC/NATIVE/…) ──
  useEffect(() => {
    if (!open || !counterCurrencyId) return;

    // Fiat (USD) has no token types to look up — it's always "native".
    if (counterCurrencyCode === "USD") {
      setTokenType("native");
      return;
    }

    setTokenType("");
    axios
      .post(
        `${WEBSERVICE}transaction/currencyTokenTypeById`,
        JSON.stringify({ currencyId: counterCurrencyId }),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      )
      .then((response) => {
        if (response.data?.error?.error_data === 0) {
          const list = response.data.response || [];
          const match = list.find((t) => t.currencyId === counterCurrencyId);
          setTokenType(match?.tokenType || "");
        }
      })
      .catch(() => setTokenType(""));
  }, [open, counterCurrencyId, counterCurrencyCode]);

  // ── Default to the first bank account whenever the popup opens
  //    (or the account list changes while it's open) ──────────
  useEffect(() => {
    if (!open) return;
    setSelectedAccountId(accounts?.[0]?.id ?? "");
  }, [open, accounts]);

  // ── Reset on open ────────────────────────────────────────────
  // NOTE: base currency selection is disabled for now — hardcoded to USD
  // (currencyId "1"). The dropdown + getBaseByCurrency fetch below are
  // commented out until multi-base-currency support is re-enabled.
  useEffect(() => {
    if (!open) return;

    setAmount("");
    setAmountError("");
    setBaseCurrencyList([]);
    setSelectedBase("USD");
    setMarketPrice(null);
    setPriceError("");
    setBuyError("");

    // if (!counterCurrencyCode) return;
    //
    // setBaseLoading(true);
    // axios
    //   .get(
    //     `${WEBSERVICE}home/getBaseByCurrency?brokerId=${localStorage.getItem(
    //       "BROKERID"
    //     )}&currency=${counterCurrencyCode}&country=${encodeURIComponent(
    //       localStorage.getItem("userCountry") || ""
    //     )}`,
    //     {
    //       headers: {
    //         "Content-Type": "application/json",
    //         authorization: `BEARER ${localStorage.getItem("access_token")}`,
    //       },
    //     }
    //   )
    //   .then((response) => {
    //     const list = response.data?.baseCurrencyList || [];
    //     setBaseCurrencyList(list);
    //     setSelectedBase(list.includes("USD") ? "USD" : list[0] || "");
    //   })
    //   .catch(() => {
    //     setBaseCurrencyList([]);
    //   })
    //   .finally(() => setBaseLoading(false));
  }, [open, counterCurrencyCode]);

  // ── Fetch market price whenever base currency changes ──────
  useEffect(() => {
    if (!open || !counterCurrencyCode || !selectedBase) return;

    setPriceError("");

    // Same currency on both sides (e.g. USD → USD) — rate is always 1,
    // no need to hit the marketPrice API.
    if (counterCurrencyCode === selectedBase) {
      setMarketPrice(1);
      setPriceLoading(false);
      return;
    }

    setPriceLoading(true);
    setMarketPrice(null);

    axios
      .post(
        `${WEBSERVICE}home/marketPrice`,
        JSON.stringify({
          currency: counterCurrencyCode,
          baseCurrency: selectedBase,
          action: 1,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      )
      .then((response) => {
        const mp = response.data?.marketPrice;
        if (response.data?.error?.error_data === 0 && mp && mp > 0) {
          setMarketPrice(mp);
        } else {
          setPriceError(
            response.data?.error?.error_msg || "Failed to fetch market price."
          );
        }
      })
      .catch(() => setPriceError("Failed to fetch market price."))
      .finally(() => setPriceLoading(false));
  }, [open, counterCurrencyCode, selectedBase]);

  const MAX_DECIMAL_PLACES = 8;

  const parsedAmount = parseFloat(amount);
  const isAmountValid = !isNaN(parsedAmount) && parsedAmount > MIN_AMOUNT;
  const counterAmount =
    isAmountValid && marketPrice
      ? parseFloat(
          (parsedAmount / marketPrice).toFixed(MAX_DECIMAL_PLACES)
        )
      : null;

  const canBuy =
    !!selectedAccountId &&
    !!selectedBase &&
    !!marketPrice &&
    isAmountValid &&
    !priceLoading &&
    !buyLoading;

  const handleAmountChange = (e) => {
    const val = e.target.value;
    setAmount(val);

    if (!val) {
      setAmountError("Amount is required.");
      return;
    }
    const num = parseFloat(val);
    if (isNaN(num) || num <= MIN_AMOUNT) {
      setAmountError(`Amount must be greater than ${MIN_AMOUNT}.`);
    } else {
      setAmountError("");
    }
  };

  const handleBuy = async () => {
    if (!canBuy) return;
    setBuyLoading(true);
    setBuyError("");

    try {
      // Base currency is hardcoded to USD (currencyId "1") for now —
      // see the commented-out getBaseByCurrency fetch above.
      const payload = {
        userUuid: userUuid || "",
        amount: String(parsedAmount),
        baseCurrencyId: "1",
        counterCurrencyId: String(counterCurrencyId ?? ""),
        counterAmount: String(counterAmount ?? ""),
        marketPrice: String(marketPrice ?? ""),
        tokenType: tokenType || "",
        plaidAccountId: String(selectedAccountId),
      };

      const response = await axios.post(
        `${WEBSERVICE}transaction/createPlaidTransfer`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      );

      if (response.data?.error?.error_data === 0) {
        showToast(
          "success",
          response.data?.error?.error_msg || "Transfer initiated successfully"
        );
        onClose();
      } else {
        setBuyError(
          response.data?.error?.error_msg || "Failed to initiate transfer."
        );
      }
    } catch (err) {
      setBuyError("Failed to initiate transfer. Please try again.");
    } finally {
      setBuyLoading(false);
    }
  };

  const logoUrl = `https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/${(
    counterCurrencyCode || ""
  ).toUpperCase()}.png`;

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: colors.surfaceAlt,
      color: colors.text,
      "& fieldset": { borderColor: colors.border },
      "&:hover fieldset": { borderColor: colors.primary },
      "&.Mui-focused fieldset": { borderColor: colors.primary, borderWidth: 2 },
    },
    "& .MuiInputLabel-root": { color: colors.textSecondary },
    "& .MuiInputLabel-root.Mui-focused": { color: colors.primary },
    "& .MuiFormHelperText-root": { color: colors.textSecondary, mt: 0.5 },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "20px",
          backgroundColor: colors.background,
          color: colors.text,
          boxShadow: isDark
            ? "0 24px 64px rgba(0,0,0,0.6)"
            : "0 24px 64px rgba(0,0,0,0.12)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: isMobile ? "100vh" : "92vh",
        },
      }}
    >
      {/* ── Gradient header band ─────────────────────────────── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${btncolor} 0%, ${alpha(
            btncolor,
            0.75
          )} 100%)`,
          px: 3,
          pt: 3,
          pb: 4,
          position: "relative",
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.7)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.12)", color: "#fff" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BoltIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}>
              Instant Buy
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
              Buy crypto instantly via Plaid
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            src={logoUrl}
            alt={counterCurrencyCode}
            sx={{ width: 40, height: 40, bgcolor: "rgba(255,255,255,0.15)" }}
          >
            {(counterCurrencyCode || "?")[0]}
          </Avatar>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.1rem" }}>
              {counterCurrencyCode || "—"}
            </Typography>
            {priceLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CircularProgress size={10} sx={{ color: "rgba(255,255,255,0.6)" }} />
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                  Fetching price…
                </Typography>
              </Box>
            ) : priceError ? (
              <Typography variant="caption" sx={{ color: "#ff8a80" }}>
                Price unavailable
              </Typography>
            ) : marketPrice ? (
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
                1 {counterCurrencyCode} ={" "}
                {marketPrice.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 8,
                })}{" "}
                {selectedBase}
              </Typography>
            ) : null}
          </Box>

          {!priceLoading && marketPrice && (
            <Chip
              icon={
                <TrendingUpIcon
                  sx={{ fontSize: "14px !important", color: "#4caf50 !important" }}
                />
              }
              label="Live Rate"
              size="small"
              sx={{
                ml: "auto",
                backgroundColor: "rgba(76,175,80,0.2)",
                color: "#81c784",
                border: "1px solid rgba(76,175,80,0.3)",
                fontWeight: 600,
                fontSize: "0.65rem",
              }}
            />
          )}
        </Box>
      </Box>

      {/* ── Scrollable content ───────────────────────────────── */}
      <DialogContent sx={{ px: 3, pt: 3, pb: 0, overflowY: "auto", flex: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* ── Account selection ───────────────────────────── */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.textSecondary,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 0.75,
                display: "block",
              }}
            >
              Select Bank Account
            </Typography>
            {accounts?.length === 0 ? (
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                No linked bank accounts found.
              </Typography>
            ) : (
              <RadioGroup
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
              >
                {accounts.map((acc) => {
                  const selected = String(selectedAccountId) === String(acc.id);
                  return (
                    <FormControlLabel
                      key={acc.id || acc.accountId}
                      value={acc.id}
                      control={<Radio size="small" sx={{ color: colors.textSecondary }} />}
                      sx={{
                        border: `1.5px solid ${selected ? colors.primary : colors.border}`,
                        borderRadius: "12px",
                        mx: 0,
                        mb: 1.25,
                        pl: 0.5,
                        pr: 1.5,
                        py: 0.75,
                        backgroundColor: selected
                          ? isDark
                            ? "rgba(144,202,249,0.1)"
                            : "rgba(25,118,210,0.06)"
                          : colors.surfaceAlt,
                        transition: "all 0.15s ease",
                        "&:hover": {
                          borderColor: colors.primary,
                        },
                      }}
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 0.25 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: isDark
                                ? "rgba(144,202,249,0.15)"
                                : "rgba(25,118,210,0.1)",
                              flexShrink: 0,
                            }}
                          >
                            <AccountBalanceIcon sx={{ fontSize: 17, color: colors.primary }} />
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: colors.text }}>
                              {acc.institutionName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                              {acc.accountType?.toUpperCase()} • {acc.accountNo}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  );
                })}
              </RadioGroup>
            )}
          </Box>

          {/* ── Base currency selection ───────────────────────
              Disabled for now — base currency is fixed to USD
              (currencyId "1"). Re-enable once multi-base-currency
              support is ready.
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.textSecondary,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 0.75,
                display: "block",
              }}
            >
              Pay With
            </Typography>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <Select
                value={selectedBase}
                onChange={(e) => setSelectedBase(e.target.value)}
                disabled={baseLoading || baseCurrencyList.length === 0}
                displayEmpty
                renderValue={(val) =>
                  val || (baseLoading ? "Loading…" : "Select currency")
                }
              >
                {baseCurrencyList.map((cur) => (
                  <MenuItem key={cur} value={cur}>
                    {cur}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          */}

          {/* ── Amount ───────────────────────────────────────── */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.textSecondary,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 0.75,
                display: "block",
              }}
            >
              Amount
            </Typography>
            <TextField
              placeholder="0.0000"
              type="number"
              value={amount}
              onChange={handleAmountChange}
              error={!!amountError}
              helperText={amountError || `Min: greater than ${MIN_AMOUNT}`}
              fullWidth
              inputProps={{ min: MIN_AMOUNT, step: "any" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      sx={{ color: colors.text, fontWeight: 700, fontSize: "0.9rem" }}
                    >
                      {selectedBase}
                    </Typography>
                  </InputAdornment>
                ),
              }}
              sx={{
                ...fieldSx,
                "& .MuiOutlinedInput-root": {
                  ...fieldSx["& .MuiOutlinedInput-root"],
                  fontSize: "1.25rem",
                  fontWeight: 600,
                },
                "& .MuiOutlinedInput-input": { py: 1.75 },
              }}
            />
          </Box>

          {/* ── Conversion preview ──────────────────────────── */}
          {counterAmount !== null && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.25,
                borderRadius: "10px",
                backgroundColor: isDark
                  ? "rgba(144,202,249,0.08)"
                  : "rgba(25,118,210,0.06)",
                border: `1px solid ${
                  isDark ? "rgba(144,202,249,0.2)" : "rgba(25,118,210,0.15)"
                }`,
              }}
            >
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                You Get
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: colors.primary, fontWeight: 700, fontSize: "1rem" }}
              >
                ≈{" "}
                {counterAmount.toLocaleString(undefined, {
                  maximumFractionDigits: 8,
                })}{" "}
                {counterCurrencyCode}
              </Typography>
            </Box>
          )}

          {priceError && (
            <Alert
              severity="warning"
              sx={{
                borderRadius: "10px",
                backgroundColor: isDark ? "rgba(255,167,38,0.1)" : undefined,
                color: isDark ? "#ffb74d" : undefined,
                "& .MuiAlert-icon": { color: isDark ? "#ffb74d" : undefined },
              }}
            >
              {priceError}
            </Alert>
          )}

          {buyError && (
            <Alert
              severity="error"
              sx={{
                borderRadius: "10px",
                backgroundColor: isDark ? "rgba(229,115,115,0.1)" : undefined,
                color: isDark ? "#ef9a9a" : undefined,
                "& .MuiAlert-icon": { color: isDark ? "#ef9a9a" : undefined },
              }}
            >
              {buyError}
            </Alert>
          )}

          <Box sx={{ pb: 1 }} />
        </Box>
      </DialogContent>

      {/* ── Actions (pinned) ─────────────────────────────────── */}
      <Box
        sx={{
          px: 3,
          pt: 2,
          pb: 3,
          flexShrink: 0,
          borderTop: `1px solid ${colors.border}`,
          backgroundColor: colors.background,
        }}
      >
        <Button
          onClick={handleBuy}
          variant="contained"
          disabled={!canBuy}
          fullWidth
          endIcon={
            buyLoading ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : undefined
          }
          sx={{
            borderRadius: "12px",
            py: 1.5,
            fontWeight: 700,
            fontSize: "0.95rem",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            background: canBuy
              ? `linear-gradient(135deg, ${btncolor} 0%, ${alpha(
                  btncolor,
                  0.75
                )} 100%)`
              : undefined,
            boxShadow: canBuy ? `0 4px 16px ${alpha(btncolor, 0.35)}` : "none",
            "&:hover": {
              background: `linear-gradient(135deg, ${alpha(
                btncolor,
                0.85
              )} 0%, ${btncolor} 100%)`,
              boxShadow: `0 6px 20px ${alpha(btncolor, 0.45)}`,
            },
            "&.Mui-disabled": {
              backgroundColor: isDark ? "#2a2a2a" : "#e0e0e0",
              color: isDark ? "#555" : "#9e9e9e",
            },
          }}
        >
          {buyLoading ? "Processing…" : "Buy"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default PlaidBuyPopup;
